import React, { useContext } from "react";
import Plot from "react-plotly.js";

import { ThemeContext } from "../../../context/ThemeContext";
import { graphThemes } from "../../../themes/graphThemes";

function snap(value) {
  return Math.round(Number(value));
}

function pointKey(point) {
  return `${point.x},${point.y}`;
}

function normalizePoints(value) {
  if (!value || typeof value !== "object" || !Array.isArray(value.points)) return [];
  return value.points
    .filter((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y))
    .map((point, index, arr) => ({
      x: point.x,
      y: point.y,
      closed: index === 0 || index === arr.length - 1 ? point.closed !== false : true,
    }));
}

function endpoints(points) {
  if (points.length === 0) return { start: null, end: null };
  return { start: points[0], end: points[points.length - 1] };
}

const GRID_CELL_SIZE = 22.5;
const PLOT_MARGIN = { t: 24, b: 36, l: 40, r: 20 };

function defaultPlotSize(range, marginBefore, marginAfter) {
  return (range[1] - range[0]) * GRID_CELL_SIZE + marginBefore + marginAfter;
}

export default function PolylineGraphInput({
  value,
  onChange,
  disabled = false,
  xRange = [-6, 6],
  yRange = [-6, 6],
  width,
  height,
  referencePoints = [],
  referenceColor = "#8b5cf6",
  answerColor = "#ff9900",
  showControls = true,
}) {
  const { theme } = useContext(ThemeContext);
  const style = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];
  const points = normalizePoints(value);
  const { start, end } = endpoints(points);

  const emit = (nextPoints) => {
    onChange?.({ points: nextPoints });
  };

  const addPoint = (event) => {
    if (disabled || !event?.points?.[0]) return;
    const next = {
      x: snap(event.points[0].x),
      y: snap(event.points[0].y),
      closed: true,
    };

    if (next.x < xRange[0] || next.x > xRange[1] || next.y < yRange[0] || next.y > yRange[1]) return;
    if (points.some((point) => pointKey(point) === pointKey(next))) return;

    emit([...points, next].sort((a, b) => a.x - b.x));
  };

  const removeLast = () => emit(points.slice(0, -1));
  const clear = () => emit([]);

  const setEndpointClosed = (side, closed) => {
    if (!points.length) return;
    const next = points.map((point, index) => {
      const isStart = side === "start" && index === 0;
      const isEnd = side === "end" && index === points.length - 1;
      return isStart || isEnd ? { ...point, closed } : point;
    });
    emit(next);
  };

  const clickGrid = {
    x: [],
    y: [],
    type: "scatter",
    mode: "markers",
    marker: { size: 28, color: "black", opacity: 0.001 },
    hoverinfo: "none",
    showlegend: false,
  };

  for (let x = xRange[0]; x <= xRange[1]; x += 1) {
    for (let y = yRange[0]; y <= yRange[1]; y += 1) {
      clickGrid.x.push(x);
      clickGrid.y.push(y);
    }
  }

  const referenceLine =
    referencePoints.length > 1
      ? {
          x: referencePoints.map((point) => point.x),
          y: referencePoints.map((point) => point.y),
          type: "scatter",
          mode: "lines",
          line: { color: referenceColor, width: 3 },
          hoverinfo: "skip",
          showlegend: false,
        }
      : null;

  const answerLine =
    points.length > 1
      ? {
          x: points.map((point) => point.x),
          y: points.map((point) => point.y),
          type: "scatter",
          mode: "lines",
          line: { color: answerColor, width: 4 },
          hoverinfo: "skip",
          showlegend: false,
        }
      : null;

  const markerPoints = (items) =>
    items.filter((point, index) => {
      if (index === 0 || index === items.length - 1) return true;
      const prev = items[index - 1];
      const next = items[index + 1];
      return (point.x - prev.x) * (next.y - point.y) !== (point.y - prev.y) * (next.x - point.x);
    });

  const splitEndpointMarkers = (items) => ({
    closed: items.filter((point, index) => {
      const isEndpoint = index === 0 || index === items.length - 1;
      return !isEndpoint || point.closed !== false;
    }),
    open: items.filter((point, index) => {
      const isEndpoint = index === 0 || index === items.length - 1;
      return isEndpoint && point.closed === false;
    }),
  });

  const referenceMarkers = splitEndpointMarkers(markerPoints(referencePoints));
  const answerMarkers = splitEndpointMarkers(markerPoints(points));

  const markerTrace = (items, open, color, size) => ({
    x: items.map((point) => point.x),
    y: items.map((point) => point.y),
    type: "scatter",
    mode: "markers",
    marker: open
      ? { size, color: style.backgroundColor, line: { color, width: 3 } }
      : { size, color },
    hoverinfo: "skip",
    showlegend: false,
  });

  const traces = [
    clickGrid,
    ...(referenceLine ? [referenceLine] : []),
    ...(referenceMarkers.closed.length ? [markerTrace(referenceMarkers.closed, false, referenceColor, 9)] : []),
    ...(referenceMarkers.open.length ? [markerTrace(referenceMarkers.open, true, referenceColor, 10)] : []),
    ...(answerLine ? [answerLine] : []),
    ...(answerMarkers.closed.length ? [markerTrace(answerMarkers.closed, false, answerColor, 11)] : []),
    ...(answerMarkers.open.length ? [markerTrace(answerMarkers.open, true, answerColor, 12)] : []),
  ];

  const xSpan = xRange[1] - xRange[0];
  const ySpan = yRange[1] - yRange[0];
  const plotWidth = width ?? defaultPlotSize(xRange, PLOT_MARGIN.l, PLOT_MARGIN.r);
  const plotHeight = height ?? defaultPlotSize(yRange, PLOT_MARGIN.t, PLOT_MARGIN.b);
  const axisAnnotations = [
    {
      x: xRange[1] + Math.max(0.18, xSpan * 0.012),
      y: 0,
      ax: xRange[1] - Math.max(0.28, xSpan * 0.022),
      ay: 0,
      xref: "x",
      yref: "y",
      axref: "x",
      ayref: "y",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: 0,
      y: yRange[1] + Math.max(0.18, ySpan * 0.012),
      ax: 0,
      ay: yRange[1] - Math.max(0.28, ySpan * 0.022),
      xref: "x",
      yref: "y",
      axref: "x",
      ayref: "y",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: xRange[1] - Math.max(0.12, xSpan * 0.01),
      y: -Math.max(0.2, ySpan * 0.018),
      xref: "x",
      yref: "y",
      text: "x",
      showarrow: false,
      font: { color: style.axisColor, size: 16, family: "Arial, sans-serif" },
      xanchor: "left",
      yanchor: "top",
    },
    {
      x: Math.max(0.35, xSpan * 0.03),
      y: yRange[1] - Math.max(0.15, ySpan * 0.012),
      xref: "x",
      yref: "y",
      text: "y",
      showarrow: false,
      font: { color: style.axisColor, size: 16, family: "Arial, sans-serif" },
      xanchor: "left",
      yanchor: "bottom",
    },
  ];

  const layout = {
    paper_bgcolor: style.backgroundColor,
    plot_bgcolor: style.backgroundColor,
    font: { color: style.axisColor },
    xaxis: {
      range: xRange.slice(),
      dtick: 1,
      fixedrange: true,
      showline: true,
      zeroline: true,
      zerolinewidth: 3,
      zerolinecolor: style.axisColor,
      gridcolor: style.gridColor,
      linecolor: style.axisColor,
      linewidth: 2,
    },
    yaxis: {
      range: yRange.slice(),
      dtick: 1,
      fixedrange: true,
      showline: true,
      zeroline: true,
      zerolinewidth: 3,
      zerolinecolor: style.axisColor,
      gridcolor: style.gridColor,
      linecolor: style.axisColor,
      linewidth: 2,
    },
    margin: PLOT_MARGIN,
    hovermode: "closest",
    showlegend: false,
    annotations: axisAnnotations,
  };

  return (
    <div>
      <div style={{ width: `min(100%, ${plotWidth}px)`, height: plotHeight, margin: "0 auto" }}>
        <Plot
          data={traces}
          layout={layout}
          config={{ scrollZoom: false, displayModeBar: false, responsive: true }}
          onClick={addPoint}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler
        />
      </div>

      {showControls ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button type="button" className="btn-secondary btn-task-action" onClick={removeLast} disabled={disabled || !points.length}>
              Cofnij punkt
            </button>
            <button type="button" className="btn-secondary btn-task-action" onClick={clear} disabled={disabled || !points.length}>
              Wyczyść
            </button>
          </div>

          {points.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 12, alignItems: "center" }}>
              <label>
                Początek:{" "}
                <select
                  className="input-secondary"
                  style={{ width: 126, minWidth: 126, padding: "3px 8px" }}
                  value={start?.closed === false ? "open" : "closed"}
                  disabled={disabled}
                  onChange={(event) => setEndpointClosed("start", event.target.value === "closed")}
                >
                  <option value="closed">domknięty</option>
                  <option value="open">otwarty</option>
                </select>
              </label>

              <label>
                Koniec:{" "}
                <select
                  className="input-secondary"
                  style={{ width: 126, minWidth: 126, padding: "3px 8px" }}
                  value={end?.closed === false ? "open" : "closed"}
                  disabled={disabled || points.length < 2}
                  onChange={(event) => setEndpointClosed("end", event.target.value === "closed")}
                >
                  <option value="closed">domknięty</option>
                  <option value="open">otwarty</option>
                </select>
              </label>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}